import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { apiRequest } from "@/lib/queryClient";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json";

// UN M.49 numeric → ISO 3166-1 alpha-2 mapping for world-atlas TopoJSON
const NUMERIC_TO_ISO2: Record<string, string> = {
  "4": "AF",
  "8": "AL",
  "12": "DZ",
  "24": "AO",
  "32": "AR",
  "36": "AU",
  "40": "AT",
  "50": "BD",
  "56": "BE",
  "68": "BO",
  "76": "BR",
  "100": "BG",
  "116": "KH",
  "120": "CM",
  "124": "CA",
  "144": "LK",
  "152": "CL",
  "156": "CN",
  "170": "CO",
  "180": "CD",
  "188": "CR",
  "191": "HR",
  "192": "CU",
  "196": "CY",
  "203": "CZ",
  "208": "DK",
  "214": "DO",
  "218": "EC",
  "818": "EG",
  "222": "SV",
  "231": "ET",
  "246": "FI",
  "250": "FR",
  "266": "GA",
  "276": "DE",
  "288": "GH",
  "300": "GR",
  "320": "GT",
  "332": "HT",
  "340": "HN",
  "348": "HU",
  "356": "IN",
  "360": "ID",
  "364": "IR",
  "368": "IQ",
  "372": "IE",
  "376": "IL",
  "380": "IT",
  "388": "JM",
  "392": "JP",
  "400": "JO",
  "404": "KE",
  "410": "KR",
  "414": "KW",
  "418": "LA",
  "422": "LB",
  "430": "LR",
  "434": "LY",
  "442": "LU",
  "484": "MX",
  "504": "MA",
  "508": "MZ",
  "516": "NA",
  "524": "NP",
  "528": "NL",
  "554": "NZ",
  "558": "NI",
  "566": "NG",
  "578": "NO",
  "586": "PK",
  "591": "PA",
  "598": "PG",
  "604": "PE",
  "608": "PH",
  "616": "PL",
  "620": "PT",
  "630": "PR",
  "634": "QA",
  "642": "RO",
  "643": "RU",
  "646": "RW",
  "682": "SA",
  "686": "SN",
  "694": "SL",
  "706": "SO",
  "710": "ZA",
  "724": "ES",
  "752": "SE",
  "756": "CH",
  "760": "SY",
  "764": "TH",
  "788": "TN",
  "792": "TR",
  "800": "UG",
  "804": "UA",
  "784": "AE",
  "826": "GB",
  "840": "US",
  "858": "UY",
  "860": "UZ",
  "862": "VE",
  "704": "VN",
  "887": "YE",
  "894": "ZM",
  "716": "ZW",
  "051": "AM",
  "031": "AZ",
  "112": "BY",
  "268": "GE",
  "398": "KZ",
  "417": "KG",
  "428": "LV",
  "440": "LT",
  "498": "MD",
  "496": "MN",
  "688": "RS",
  "703": "SK",
  "705": "SI",
  "762": "TJ",
  "795": "TM",
  "233": "EE",
  "070": "BA",
  "807": "MK",
  "499": "ME",
  "008": "AL",
  "020": "AD",
  "064": "BT",
  "096": "BN",
  "136": "KY",
  "174": "KM",
  "178": "CG",
  "226": "GQ",
  "262": "DJ",
  "480": "MU",
  "462": "MV",
  "466": "ML",
  "478": "MR",
  "600": "PY",
  "690": "SC",
  "740": "SR",
  "780": "TT",
};

// ── Types ─────────────────────────────────────────────────────────────────────

export type GeoLocationType = "country" | "region" | "city" | "zip";
export type GeoTargetMode = "include" | "exclude";

export interface GeoLocationResult {
  key: string;
  name: string;
  type: GeoLocationType;
  countryCode: string;
  countryName: string;
  region?: string;
  regionId?: string;
}

export interface SelectedGeoLocation extends GeoLocationResult {
  mode: GeoTargetMode;
}

export interface GeoTargetingValue {
  included: SelectedGeoLocation[];
  excluded: SelectedGeoLocation[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const TYPE_LABEL_KEYS: Record<GeoLocationType, string> = {
  country: "geoTargeting.type.country",
  region: "geoTargeting.type.region",
  city: "geoTargeting.type.city",
  zip: "geoTargeting.type.zip",
};

// ── Sub-components ────────────────────────────────────────────────────────────

interface ModeToggleProps {
  mode: GeoTargetMode;
  onChange: (mode: GeoTargetMode) => void;
}

function ModeToggle({ mode, onChange }: ModeToggleProps) {
  const { t } = useTranslation();
  return (
    <div className="flex rounded-md overflow-hidden border border-white/10 text-xs">
      <button
        type="button"
        onClick={() => onChange("include")}
        className={`flex-1 px-3 py-1.5 transition-colors ${
          mode === "include"
            ? "bg-green-500/20 text-green-400 border-r border-green-500/30"
            : "text-white/40 hover:text-white/60 border-r border-white/10"
        }`}
      >
        {t("geoTargeting.add")}
      </button>
      <button
        type="button"
        onClick={() => onChange("exclude")}
        className={`flex-1 px-3 py-1.5 transition-colors ${
          mode === "exclude" ? "bg-red-500/20 text-red-400" : "text-white/40 hover:text-white/60"
        }`}
      >
        {t("geoTargeting.exclude")}
      </button>
    </div>
  );
}

interface SearchResult {
  results: GeoLocationResult[];
  isLoading: boolean;
  query: string;
  error: string | null;
}

interface LocationSearchInputProps {
  searchState: SearchResult;
  query: string;
  onQueryChange: (q: string) => void;
  onSelect: (location: GeoLocationResult) => void;
  onClose: () => void;
  pendingMode: GeoTargetMode;
}

function LocationSearchInput({
  searchState,
  query,
  onQueryChange,
  onSelect,
  onClose,
  pendingMode,
}: LocationSearchInputProps) {
  const { t } = useTranslation();
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBlur = () => {
    blurTimerRef.current = setTimeout(onClose, 150);
  };

  const handleResultMouseDown = (loc: GeoLocationResult) => (e: React.MouseEvent) => {
    e.preventDefault(); // prevent input blur before click registers
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    onSelect(loc);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onBlur={handleBlur}
        placeholder={t("geoTargeting.searchPlaceholder")}
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/30"
      />
      {(searchState.results.length > 0 || searchState.isLoading || !!searchState.error) &&
        query.length >= 2 && (
          <div className="absolute z-50 top-full mt-1 w-full bg-[#1a1d23] border border-white/10 rounded-lg shadow-xl overflow-hidden">
            {searchState.isLoading && (
              <div className="px-3 py-2 text-xs text-white/40">{t("geoTargeting.searching")}</div>
            )}
            {searchState.error && !searchState.isLoading && (
              <div className="px-3 py-2 text-xs text-yellow-400/80">{searchState.error}</div>
            )}
            {searchState.results.map((loc) => (
              <button
                key={`${loc.type}-${loc.key}`}
                type="button"
                onMouseDown={handleResultMouseDown(loc)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-white/5 transition-colors"
              >
                <span
                  className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                    loc.type === "country"
                      ? "text-blue-400 border-blue-500/30 bg-blue-500/10"
                      : loc.type === "region"
                        ? "text-purple-400 border-purple-500/30 bg-purple-500/10"
                        : "text-yellow-400 border-yellow-500/30 bg-yellow-500/10"
                  }`}
                >
                  {t(TYPE_LABEL_KEYS[loc.type])}
                </span>
                <span className="text-white truncate">{loc.name}</span>
                {loc.type !== "country" && (
                  <span className="text-white/40 text-xs ml-auto shrink-0">{loc.countryName}</span>
                )}
                <span
                  className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded ${
                    pendingMode === "include"
                      ? "text-green-400 bg-green-500/10"
                      : "text-red-400 bg-red-500/10"
                  }`}
                >
                  {pendingMode === "include"
                    ? t("geoTargeting.addBadge")
                    : t("geoTargeting.excludeBadge")}
                </span>
              </button>
            ))}
          </div>
        )}
    </div>
  );
}

interface LocationChipListProps {
  included: SelectedGeoLocation[];
  excluded: SelectedGeoLocation[];
  onRemove: (key: string, type: GeoLocationType, mode: GeoTargetMode) => void;
}

function LocationChipList({ included, excluded, onRemove }: LocationChipListProps) {
  const { t } = useTranslation();
  if (included.length === 0 && excluded.length === 0) return null;

  const renderChip = (loc: SelectedGeoLocation) => (
    <span
      key={`${loc.mode}-${loc.type}-${loc.key}`}
      className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border ${
        loc.mode === "include"
          ? "bg-green-500/15 border-green-500/30 text-green-300"
          : "bg-red-500/15 border-red-500/30 text-red-300"
      }`}
    >
      <span className="opacity-60 text-[10px]">{t(TYPE_LABEL_KEYS[loc.type])}</span>
      <span>{loc.name}</span>
      <button
        type="button"
        onClick={() => onRemove(loc.key, loc.type, loc.mode)}
        className="opacity-60 hover:opacity-100 transition-opacity ml-0.5"
        aria-label={t("geoTargeting.removeAria", { name: loc.name })}
      >
        ×
      </button>
    </span>
  );

  return (
    <div className="flex flex-wrap gap-1.5">
      {included.map(renderChip)}
      {excluded.map(renderChip)}
    </div>
  );
}

interface GeoMapProps {
  includedCodes: Set<string>;
  excludedCodes: Set<string>;
}

function GeoMap({ includedCodes, excludedCodes }: GeoMapProps) {
  const getFill = (numericId: string): string => {
    const iso2 = NUMERIC_TO_ISO2[numericId] ?? NUMERIC_TO_ISO2[numericId.replace(/^0+/, "")];
    if (iso2 && includedCodes.has(iso2)) return "#22c55e33";
    if (iso2 && excludedCodes.has(iso2)) return "#ef444433";
    return "#1a1d23";
  };

  const getStroke = (numericId: string): string => {
    const iso2 = NUMERIC_TO_ISO2[numericId] ?? NUMERIC_TO_ISO2[numericId.replace(/^0+/, "")];
    if (iso2 && includedCodes.has(iso2)) return "#22c55e";
    if (iso2 && excludedCodes.has(iso2)) return "#ef4444";
    return "#2d3139";
  };

  return (
    <div
      className="w-full overflow-hidden rounded-lg border border-white/10"
      style={{ aspectRatio: "16/7" }}
    >
      <ComposableMap
        projectionConfig={{ scale: 130, center: [10, 20] }}
        style={{ width: "100%", height: "100%" }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }: { geographies: Array<{ rsmKey: string; id?: string | number }> }) =>
            geographies.map((geo: { rsmKey: string; id?: string | number }) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill={getFill(String(geo.id))}
                stroke={getStroke(String(geo.id))}
                strokeWidth={0.5}
                style={{
                  default: { outline: "none" },
                  hover: { outline: "none" },
                  pressed: { outline: "none" },
                }}
              />
            ))
          }
        </Geographies>
      </ComposableMap>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

interface GeoTargetingWidgetProps {
  value: GeoTargetingValue;
  onChange: (value: GeoTargetingValue) => void;
  adAccountId?: string;
}

export default function GeoTargetingWidget({ value, onChange }: GeoTargetingWidgetProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<GeoLocationResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [pendingMode, setPendingMode] = useState<GeoTargetMode>("include");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const includedCodes = useMemo(
    () => new Set(value.included.map((l) => l.countryCode)),
    [value.included]
  );
  const excludedCodes = useMemo(
    () => new Set(value.excluded.map((l) => l.countryCode)),
    [value.excluded]
  );

  const fetchLocations = useCallback(async (q: string) => {
    if (q.length < 2) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }
    setIsLoading(true);
    setSearchError(null);
    try {
      const data = (await apiRequest(
        "GET",
        `/api/campaign-creator/search-geo-locations?q=${encodeURIComponent(q)}`
      )) as { locations: GeoLocationResult[]; error?: string };
      setSearchResults(data.locations ?? []);
      if (data.error) setSearchError(data.error);
    } catch {
      setSearchResults([]);
      setSearchError(t("geoTargeting.connectionError"));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchLocations(query), 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, fetchLocations]);

  const closeDropdown = useCallback(() => {
    setSearchResults([]);
    setQuery("");
  }, []);

  const addLocation = useCallback(
    (location: GeoLocationResult) => {
      const exists = [...value.included, ...value.excluded].some(
        (l) => l.key === location.key && l.type === location.type
      );
      if (exists) return;
      const selected: SelectedGeoLocation = { ...location, mode: pendingMode };
      onChange({
        included: pendingMode === "include" ? [...value.included, selected] : value.included,
        excluded: pendingMode === "exclude" ? [...value.excluded, selected] : value.excluded,
      });
      setQuery("");
      setSearchResults([]);
    },
    [value, pendingMode, onChange]
  );

  const removeLocation = useCallback(
    (key: string, type: GeoLocationType, mode: GeoTargetMode) => {
      const filter = (list: SelectedGeoLocation[]) =>
        list.filter((l) => !(l.key === key && l.type === type));
      onChange(
        mode === "include"
          ? { ...value, included: filter(value.included) }
          : { ...value, excluded: filter(value.excluded) }
      );
    },
    [value, onChange]
  );

  const searchState: SearchResult = {
    results: searchResults,
    isLoading,
    query,
    error: searchError,
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <LocationSearchInput
            searchState={searchState}
            query={query}
            onQueryChange={setQuery}
            onSelect={addLocation}
            onClose={closeDropdown}
            pendingMode={pendingMode}
          />
        </div>
        <ModeToggle mode={pendingMode} onChange={setPendingMode} />
      </div>

      <LocationChipList
        included={value.included}
        excluded={value.excluded}
        onRemove={removeLocation}
      />

      <GeoMap includedCodes={includedCodes} excludedCodes={excludedCodes} />
    </div>
  );
}
